import React, { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuthentication } from "./useAuthentication.ts";
import { getCookie } from "../utils/cookies.ts";
import AlertModal from "../components/AlertModal.tsx";

interface ProtectedRouteProps {
  children?: ReactNode;
  element?: React.ReactElement;
  fallbackRoute?: string;
  requireAuth?: boolean;
  requiredRoles?: string[];
  requiredPermissions?: string[];
  loginUrl?: string;
  unauthorizedComponent?: ReactNode;
}

// Protected Route Component
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  element,
  fallbackRoute,
  requireAuth = true,
  requiredRoles = [],
  requiredPermissions = [],
  unauthorizedComponent,
}) => {
  const { user, isAuthenticated, isLoading, refreshAuth } = useAuthentication();
  const [isRefreshAttempted, setIsRefreshAttempted] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Attempt to refresh authentication if not authenticated but has refresh token
  useEffect(() => {
    const attemptRefresh = async () => {
      if (
        requireAuth &&
        !isAuthenticated &&
        !isLoading &&
        !isRefreshAttempted &&
        getCookie("refreshToken")
      ) {
        setIsRefreshing(true);
        setIsRefreshAttempted(true);

        console.log("ProtectedRoute: Attempting to refresh authentication...");
        const refreshSuccess = await refreshAuth();

        if (!refreshSuccess) {
          console.log("ProtectedRoute: Authentication refresh failed");
        }

        setIsRefreshing(false);
      } else if (
        requireAuth &&
        !isAuthenticated &&
        !isLoading &&
        !isRefreshAttempted &&
        !getCookie("refreshToken")
      ) {
        // No refresh token available, send them to the local login page
        console.log("ProtectedRoute: No refresh token, redirecting to login...");
        setIsRefreshAttempted(true);
        navigate("/login", { state: { from: location }, replace: true });
      }
    };

    attemptRefresh();
  }, [
    requireAuth,
    isAuthenticated,
    isLoading,
    isRefreshAttempted,
    refreshAuth,
    fallbackRoute,
    navigate,
    location,
  ]);

  // Show loading while initial authentication check or refresh is in progress
  if (isLoading || isRefreshing) {
    const handleContinue = () => {
      setShowAuthModal(false);
      if (fallbackRoute) {
        navigate(fallbackRoute);
      }
    };
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600 mx-auto"></div>
          <AlertModal
            show={showAuthModal}
            onClose={() => { }}
            title="Authentication Required"
            message="This feature is available for authenticated users. Please log in to use it."
            confirmText="Continue"
            onConfirm={handleContinue}
            showCancel={false}
          />
          {unauthorizedComponent}
          <p className="mt-4 text-gray-600">
            {isRefreshing
              ? "Refreshing authentication..."
              : "Checking authentication..."}
          </p>
        </div>
      </div>
    );
  }

  // Check authentication after refresh attempt
  if (requireAuth && !isAuthenticated) {
    // Only attempt refresh if we have a refresh token and haven't tried yet
    if (isRefreshAttempted) {
      // Show authentication modal instead of immediate redirect
      const handleContinue = () => {
        setShowAuthModal(false);
        if (fallbackRoute) {
          navigate(fallbackRoute);
        }
      };

      // Show modal first, then handle continue action
      if (!showAuthModal) {
        setShowAuthModal(true);
      }

      return (
        <>
          <AlertModal
            show={showAuthModal}
            onClose={() => { }}
            title="Authentication Required"
            message="This feature is available for authenticated users. Please log in to use it."
            confirmText="Continue"
            onConfirm={handleContinue}
            showCancel={false}
          />
          {unauthorizedComponent}
        </>
      );
    }

    const handleContinue = () => {
      setShowAuthModal(false);
      if (fallbackRoute) {
        navigate(fallbackRoute);
      }
    };

    // If we haven't attempted refresh yet, show loading
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600 mx-auto"></div>

          <p className="mt-4 text-gray-600">Checking authentication...</p>
          <AlertModal
            show={showAuthModal}
            onClose={() => { }}
            title="Authentication Required"
            message="This feature is available for authenticated users. Please log in to use it."
            confirmText="Continue"
            onConfirm={handleContinue}
            showCancel={false}
          />
        </div>
      </div>
    );
  }

  // If user is authenticated, check authorization
  if (isAuthenticated && user) {
    const hasRequiredRoles =
      requiredRoles.length === 0 ||
      requiredRoles.some((role) => user.roles.includes(role));

    const hasRequiredPermissions =
      requiredPermissions.length === 0 ||
      requiredPermissions.some((permission) =>
        user.permissions.includes(permission)
      );

    // Check if user has required roles or permissions
    if (!hasRequiredRoles || !hasRequiredPermissions) {
      const handleContinue = () => {
        setShowAuthModal(false);
        if (fallbackRoute) {
          navigate(fallbackRoute);
        }
      };

      // Show modal first, then handle continue action
      if (!showAuthModal) {
        setShowAuthModal(true);
      }

      return (
        <>
          <AlertModal
            show={showAuthModal}
            onClose={() => { }}
            title="Missing Permissions"
            message="You do not have the required permissions to access this feature."
            confirmText="Continue"
            onConfirm={handleContinue}
            showCancel={false}
          />
          {unauthorizedComponent}
        </>
      );
    }
  }

  // If all checks pass, render the protected content
  return element ? element : <>{children}</>;
};

// Props for role-based protection
interface RoleGuardProps {
  children: ReactNode;
  allowedRoles: string[];
  fallbackComponent?: ReactNode;
  requireAll?: boolean; // Whether user needs ALL roles or just one
}

// Role Guard Component
export const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  allowedRoles,
  fallbackComponent,
  requireAll = false,
}) => {
  const { user, isAuthenticated } = useAuthentication();

  if (!isAuthenticated || !user) {
    return fallbackComponent || <div>Access denied: Not authenticated</div>;
  }

  const hasAccess = requireAll
    ? allowedRoles.every((role) => user.roles.includes(role))
    : allowedRoles.some((role) => user.roles.includes(role));

  if (!hasAccess) {
    return (
      fallbackComponent || <div>Access denied: Insufficient permissions</div>
    );
  }

  return <>{children}</>;
};

// Props for permission-based protection
interface PermissionGuardProps {
  children: ReactNode;
  requiredPermissions: string[];
  fallbackComponent?: ReactNode;
  fallbackRoute?: string;
  requireAll?: boolean; // Whether user needs ALL permissions or just one
}

// Permission Guard Component
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  requiredPermissions,
  fallbackComponent,
  fallbackRoute,
  requireAll = false,
}) => {
  const { user, isAuthenticated } = useAuthentication();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const navigate = useNavigate();

  if (!isAuthenticated || !user) {
    const handleContinue = () => {
      setShowAuthModal(false);
      if (fallbackRoute) {
        navigate(fallbackRoute);
      }
    };

    if (!showAuthModal && (fallbackComponent || fallbackRoute)) {
      setShowAuthModal(true);
    }

    return (
      <>
        {(fallbackComponent || fallbackRoute) && (
          <AlertModal
            show={showAuthModal}
            onClose={() => { }}
            title="Authentication Required"
            message="This feature is available for authenticated users. Please log in to use it."
            confirmText="Continue"
            onConfirm={handleContinue}
            showCancel={false}
          />
        )}
        {fallbackComponent ||
          (fallbackRoute ? (
            <Navigate to={fallbackRoute} replace />
          ) : (
            <div>Access denied: Not authenticated</div>
          ))}
      </>
    );
  }

  const hasAccess = requireAll
    ? requiredPermissions.every((permission) =>
      user.permissions.includes(permission)
    )
    : requiredPermissions.some((permission) =>
      user.permissions.includes(permission)
    );

  if (!hasAccess) {
    return (
      fallbackComponent || <div>Access denied: Insufficient permissions</div>
    );
  }

  return <>{children}</>;
};
